import { useState } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { IoCloseSharp } from "react-icons/io5";
import { createTag, getPropertyTags } from "../../../../services/TagsService";
import toast from "react-hot-toast";
import { FIVE_MINUTES } from "../../../../constants/time";
import isNull from "lodash/isNull";
import TagList from "../../../../components/common/TagList";
import useMixpanelEvent from "../../../../hooks/useMixpanelEvent";
import useLocalStorage from "../../../../hooks/useLocalStorage";
import { useUser } from "../../../../context/UserContext";
import { useAuth0 } from "@auth0/auth0-react";

const HashtagModal = ({ userId, propertyId, show, onClose }) => {
    const [inputValue, setInputValue] = useState("");
    const [userTags, setUserTags] = useState([]);
    const queryClient = useQueryClient();
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const [socialState] = useLocalStorage("social-state");
    const { userState } = useUser();
    const { isAuthenticated } = useAuth0();
    const isListingPage = window.location.pathname.includes("/listing/");

    const { data: tagsData } = useQuery({
        queryKey: ["propertyTags", propertyId],
        queryFn: () => getPropertyTags(propertyId),
        enabled: !isNull(propertyId),
        staleTime: FIVE_MINUTES,
    });

    const { mutate: submitTag } = useMutation({
        mutationFn: ({ name }) => createTag({ name, userId, propertyId }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: ["propertyTags", propertyId],
            });

            // Track Create Hashtag event on successful creation
            let pageName = "Property Feed - Trending";
            if (isListingPage) {
                pageName = "Property Details";
            } else if (socialState?.activeTab === "following") {
                pageName = "Property Feed - Following";
            }

            sendMixpanelEvent("Create Hashtag", {
                pageName,
                hashtagName: variables.name,
                user_id: userId,
                nickname: userState?.nickname,
                email: userState?.email,
                isAuthenticated
            });
        },
        onError: (error) => {
            console.error("Error submitting tag:", error);
            toast.error("Failed to add tag");
        },
    });

    const addTag = (rawValue) => {
        const rawTag = rawValue.trim().toLowerCase();
        const sanitizedTag = rawTag.replace(/[^a-z0-9]/gi, "");

        if (!sanitizedTag) {
            toast.error("Tags must contain letters or numbers.");
            setInputValue("");
            return;
        }

        if (!userTags.includes(sanitizedTag)) {
            setUserTags([...userTags, sanitizedTag]);
            submitTag({ name: sanitizedTag });
        }

        setInputValue("");
    };

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
    };

    const handleBlur = () => {
        if (inputValue.trim()) {
            addTag(inputValue);
        }
    };

    const handleKeyDown = (e) => {
        const isSubmitKey = e.key === "Enter" || e.code === "Enter" || e.key === " " || e.key === ",";

        if (isSubmitKey && inputValue.trim()) {
            e.preventDefault();
            addTag(inputValue);
        }
    };

    const removeUserTag = (tagToRemove) => {
        setUserTags(userTags.filter((tag) => tag !== tagToRemove));
    };

    //const currentUserTags = tagsData?.data?.filter((tag) => tag.creator?.id === userId) || [];
    const otherTags = tagsData?.data?.filter((tag) => tag.creator?.id !== userId) || [];

    if (!show) return null;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="tw-fixed tw-inset-0 tw-bg-black/40 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-p-4"
        >
            <div className="tw-bg-white tw-rounded-2xl tw-w-full tw-max-w-md tw-p-5 tw-relative tw-shadow-lg">
                {/* Close Button */}
                <IoCloseSharp
                    onClick={onClose}
                    className="tw-absolute tw-top-4 tw-right-4 tw-text-xl tw-cursor-pointer tw-text-gray-600 hover:tw-text-black"
                />

                <h3 className="tw-text-xl tw-font-semibold tw-text-center tw-mb-4">Add hashtags</h3>

                <div className="tw-w-full tw-border tw-border-gray-300 tw-rounded-xl tw-p-2 tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-min-h-[44px] tw-mb-4">
                    {userTags.map((tag) => (
                        <span
                            key={tag}
                            className="tw-bg-purple-100 tw-text-purple-800 tw-px-3 tw-py-1 tw-rounded-full tw-text-sm tw-flex tw-items-center gap-1"
                        >
                            #{tag}
                            <IoCloseSharp
                                className="tw-cursor-pointer hover:tw-text-purple-900"
                                onClick={() => removeUserTag(tag)}
                            />
                        </span>
                    ))}
                    <input
                        type="text"
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        onBlur={handleBlur}
                        className="tw-flex-1 tw-min-w-[80px] tw-border-none tw-outline-none tw-text-sm"
                        placeholder="Type a tag, press space or comma to add"
                        maxLength={50}
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                    />
                </div>

                {/* <p className="tw-text-sm tw-text-gray-500 tw-mb-1">Your Tags:</p>
                <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mb-4">
                    {currentUserTags.map((tag) => (
                        <span
                            key={tag.id}
                            className="tw-bg-purple-100 tw-text-purple-800 tw-px-3 tw-py-1 tw-rounded-full tw-text-sm tw-flex tw-items-center gap-1"
                        >
                            #{tag.name}
                            <IoCloseSharp
                                className="tw-cursor-pointer hover:tw-text-purple-900"
                                onClick={() => removeTag(tag.id)}
                            />
                        </span>
                    ))}
                </div> */}

                {otherTags.length > 0 && (
                    <>
                        <p className="tw-text-sm tw-text-gray-500 tw-mb-1">Tags From Others:</p>
                        <TagList tags={otherTags} propertyId={propertyId} />
                    </>
                )}

                <div className="tw-flex tw-justify-center tw-mt-6">
                    <button
                        className="tw-bg-gradient-to-r tw-from-orange-400 tw-to-orange-500 tw-text-white tw-rounded-lg tw-py-2 tw-px-6 tw-font-semibold"
                        onClick={onClose}
                    >
                        Done
                    </button>
                </div>
            </div>
        </motion.div>
    );
};

export default HashtagModal;
